import { useEffect, useState } from 'react';
import T from 'i18n-react';

import texts from 'Components/texts.json';
import type { ListType } from 'Types/listType';
import { LIST_TYPES } from 'Utilities/consts';
import dayjs from 'Utilities/dayjs';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';

T.setTexts(texts);

type ListTypes = keyof typeof LIST_TYPES;
interface TranslationConfig {
	key: string;
	params?: Record<string, string>;
}

type TitleMap = { [key in ListTypes]: TranslationConfig };
interface UseNoSaleTitleProps {
	listType: ListType;
	titleProp?: string;
}

const titleForNonNextSaleTab = 'vehicleList.newVehiclesSoonOnNextSale';
const titleForShortlist = 'vehicleList.noShortlistTitle';

export const FALLBACK_SALE_TIME = '-:--';

const useNoSaleTitle = ({ listType, titleProp }: UseNoSaleTitleProps): string => {
	const { hasReceivedSaleTimes, isSaleActive, isSaleLaterToday, nextSaleStartsAt } = useSaleTimes();
	const [nextSaleTimeDisplayValue, setNextSaleTimeDisplayValue] = useState(FALLBACK_SALE_TIME);
	const isRemoveBuyItNowFeatureEnabled = useFeatureToggle(FEATURES.removeBuyItNow);
	const isVehicleListDisabled = useFeatureToggle(FEATURES.showDownloadCSVButtonOutsideList);

	const shouldShowLaterTodayTitle = Boolean(
		isSaleLaterToday && nextSaleStartsAt && hasReceivedSaleTimes && !isSaleActive,
	);

	useEffect(() => {
		if (shouldShowLaterTodayTitle) {
			const saleStartTime = dayjs(nextSaleStartsAt);
			const nextSaleStartsAtFormatted =
				saleStartTime.minute() === 0 ? saleStartTime.format('ha') : saleStartTime.format('h:mma');
			setNextSaleTimeDisplayValue(nextSaleStartsAtFormatted);
		}
	}, [nextSaleStartsAt, shouldShowLaterTodayTitle, isSaleActive]);

	const titleForSaleLaterToday = {
		key: isRemoveBuyItNowFeatureEnabled
			? 'vehicleList.binRemovedNewVehiclesLaterToday'
			: 'vehicleList.newVehiclesLaterToday',
		params: { saleStart: nextSaleTimeDisplayValue },
	};

	const defaultTitle = '';
	const titleMap: TitleMap = {
		[LIST_TYPES.auction]: shouldShowLaterTodayTitle ? titleForSaleLaterToday : { key: titleForNonNextSaleTab },
		[LIST_TYPES.buyItNow]: shouldShowLaterTodayTitle ? titleForSaleLaterToday : { key: titleForNonNextSaleTab },
		[LIST_TYPES.shortlist]: { key: titleForShortlist },
		[LIST_TYPES.aiCompanion]: shouldShowLaterTodayTitle ? titleForSaleLaterToday : { key: titleForNonNextSaleTab },
	};

	const titleConfig = listType && titleMap[listType] ? titleMap[listType] : { key: defaultTitle };

	const title = `${T.translate(titleConfig.key, titleConfig.params || {})}`;

	if (isVehicleListDisabled) return 'Vehicle list is currently down';

	return titleProp || title;
};

export default useNoSaleTitle;
